import styled from 'styled-components';
import { breakpoints, sizes, theme } from '../../theme';
import InformationIcon from '../../assets/icons/indicators/InformationIcon';
import { BodyLarge, BodyRegular } from '../Elements';
import strings from '../../strings/strings.json';
import { ACCESSIBILITY_GUIDE } from '../accessibilityGuide/AccessibilityGuide';
import useWindowWidth from '../../hooks/useWindowWidth';
import Button from '../buttons/Button';
import * as React from 'react';
import { StandardLink } from 'components/StandardLink';

export default function AccessibilityNotifier({
    accessibilityGuideKey,
    fixButtonFunction,
    fixButtonName,
    fixButtonAria,
    fixButtonIcon,
}: {
    accessibilityGuideKey: string;
    fixButtonFunction?: Function;
    fixButtonName?: string;
    fixButtonAria?: string;
    fixButtonIcon?: JSX.Element;
}): JSX.Element {
    const { currentWindowWidth } = useWindowWidth();

    const accessibilityTip = ACCESSIBILITY_GUIDE.find((x) => x.id === accessibilityGuideKey);

    if (!accessibilityTip) return <></>;

    return (
        <NotificationContainer>
            <TitleSection>
                <TitleText>
                    {currentWindowWidth >= parseInt(breakpoints.md) && (
                        <IconContainer>
                            <InformationIcon colour={theme.textColour} />
                        </IconContainer>
                    )}
                    <BodyLarge $bold>{accessibilityTip.title}</BodyLarge>
                </TitleText>
                <AccessibilityTagSection>
                    {currentWindowWidth < parseInt(breakpoints.md) && (
                        <IconContainer>
                            <InformationIcon colour={theme.textColour} />
                        </IconContainer>
                    )}
                    <AccessibilityTag>{strings.general.accessibilityTagForNotification}</AccessibilityTag>
                </AccessibilityTagSection>
            </TitleSection>
            {typeof accessibilityTip.description === 'string' ? (
                <DescriptionSection>{accessibilityTip.description}</DescriptionSection>
            ) : (
                accessibilityTip.description
            )}

            {accessibilityTip.readMore && (
                <ExternalLinkContainer>
                    <StandardLink linkUrl={accessibilityTip.readMore} linkText={strings.general.readMore} />
                </ExternalLinkContainer>
            )}
            {fixButtonFunction && fixButtonName && fixButtonAria && (
                <Button
                    title={fixButtonName}
                    aria={fixButtonAria}
                    onClick={() => fixButtonFunction()}
                    icon={fixButtonIcon}
                    fullwidth
                />
            )}
        </NotificationContainer>
    );
}

const NotificationContainer = styled.div`
    @media (max-width: ${breakpoints.sm}) {
        width: 100%;
    }
    max-width: 60rem;
    display: flex;
    flex-direction: column;
    border-radius: ${sizes.borderRadiusMd};
    padding: ${sizes.spacingMd};
    gap: ${sizes.spacingMd};
    border: solid 3px ${theme.notificationAccessibilityBorder};
    background: ${theme.notificationAccessibilityBackground};
`;

const IconContainer = styled.div`
    display: flex;
    align-items: center;
`;

const DescriptionSection = styled(BodyRegular)`
    white-space: pre-wrap;
`;

const TitleText = styled.div`
    display: flex;
    gap: ${sizes.spacingMd};
    justify-content: flex-start;
    align-items: center;
`;

const TitleSection = styled.div`
    display: flex;
    gap: ${sizes.spacingMd};
    justify-content: space-between;
    align-items: flex-start;
    @media (max-width: ${breakpoints.md}) {
        flex-direction: column-reverse;
        justify-content: flex-start;
        gap: ${sizes.spacingSm};
    }
`;

const AccessibilityTag = styled.div`
    padding: ${sizes.spacingXs} ${sizes.spacingSm};
    border-radius: ${sizes.borderRadiusMd};
    background: ${theme.notificationWarningBackground};
`;

const AccessibilityTagSection = styled.div`
    display: flex;
    gap: ${sizes.spacingMd};
    justify-content: flex-start;
    align-items: center;
`;

const ExternalLinkContainer = styled.div`
    display: flex;
`;
